import React, { useState, useEffect } from 'react';
import { useModal } from 'hooks';
import { ExpandMoreOutlined, ChevronRight } from '@mui/icons-material';
import { TreeItem, SimpleTreeView } from '@mui/x-tree-view';
import { validatePermission } from 'actions/index';
import ApplicationLayout from 'v3/layouts/Application';
import { useSnackbar } from 'v3/components/Snackbar';
import { Button, Text } from 'v3/components';
import { cooperplaceApi } from 'services/api';
import { usePermission } from 'hooks';
import CardCostCenter from './Card';
import { Row, Col } from 'react-bootstrap';
import { Divider } from '@mui/material';
import { TreeContainer } from './Card/style';
import { FaFileAlt, FaRegFolder, FaRegFolderOpen } from "react-icons/fa";
import Loading from 'components/Loading';
import { Skeleton } from '@mui/material';
import Filters from './Filter';
import ConfirmationModal from '../shared/Modal/ConfirmationModal';
import { useParams } from 'react-router-dom';
import { fetchCostCentersByIds } from 'utils/fetches'

function CostCenter() {
  const snackbar = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [loadingCostCenters, setLoadingCostCenters] = useState(false);
  const [costCenters, setCostCenters] = useState([]);
  const [selectCostCenter, setSelectCostCenter] = useState({});
  const [confirmedSelection, setConfirmedSelection] = useState(null);
  const [pendingSelectionId, setPendingSelectionId] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState([]);
  const [filters, setFilters] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [pendingCostCenter, setPendingCostCenter] = useState(null);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [initialParamApplied, setInitialParamApplied] = useState(false);

  const params = useParams()

  const confirmationModal = useModal();

  const hasPermissionToSeeCostCenters = usePermission(
    'VISUALIZAR_CENTRO_CUSTO',
    { redirect: true }
  );

  function getNodePath(rootNodes, targetId, path = []) {
    for (const node of rootNodes) {
      const newPath = [...path, String(node.id)];

      if (node.id === targetId) return newPath;

      const nextLevelKey = Object.keys(node).find(
        key => key.startsWith('costCentersLevel') && Array.isArray(node[key])
      );

      if (nextLevelKey) {
        const res = getNodePath(node[nextLevelKey], targetId, newPath);
        if (res) return res;
      }
    }
    return null;
  }


  async function fetchCostCenters() {
    try {
      if (!hasPermissionToSeeCostCenters) return;
      setLoading(true);
      const response = await cooperplaceApi.get(`/cost-center`, {
        params: {
          ccIds:
            filters?.costCenter?.length > 0
              ? filters?.costCenter?.map(cc => cc.id)
              : null,
        },
      });
      setCostCenters(response.data || []);
    } catch (error) {
      console.error('fetchCostCenters error', error);
      snackbar.show(
        <Text>Algo deu errado, tentar novamente mais tarde</Text>,
        { type: 'error' }
      );
    } finally {
      setLoading(false);
      setLoadingInitial(false);
    }
  }

  useEffect(() => {
    fetchCostCenters();
  }, [hasPermissionToSeeCostCenters, filters]);

  useEffect(() => {

    async function loadInitialSelection() {

      if (!params?.id || initialParamApplied || costCenters.length === 0) return;

      const idNumber = Number(params.id);
      const findCc = await fetchCostCentersByIds([idNumber]);

      if (findCc.length > 0) {
        const selected = findCc[0];

        setSelectCostCenter(selected);
        setConfirmedSelection(String(selected.id));

        const pathToNode = getNodePath(costCenters, selected.id);
        if (pathToNode) {
          setExpandedNodes(pathToNode);
        }
      }

      setInitialParamApplied(true);
    }

    loadInitialSelection();
  }, [params?.id, costCenters, initialParamApplied]);


  async function getAndUpdateCostCentersBenner() {
    try {
      setLoadingCostCenters(true);
      setLoading(true);
      const response = await cooperplaceApi.put('/cost-center/benner');
      if (response.data?.success) {
        snackbar.show(
          <Text>Centros de custo importados com sucesso!</Text>,
          { type: 'success' }
        );
        await fetchCostCenters();
      } else {
        snackbar.show(
          <Text>Não foi possível importar Centros de Custo da Benner</Text>,
          { type: 'error' }
        );
      }
    } catch (error) {
      console.error(error);
      snackbar.show(
        <Text>Não foi possível importar Centros de Custo da Benner</Text>,
        { type: 'error' }
      );
    } finally {
      setLoadingCostCenters(false);
      setLoading(false);
    }
  }

  const handleTreeItemClick = (cc) => {
    const clickedId = cc?.id ? String(cc.id) : null;
    const currentSelectedId = selectCostCenter?.id ? String(selectCostCenter.id) : null;

    if (currentSelectedId && clickedId === currentSelectedId) return;

    if (editMode) {
      setPendingCostCenter(cc);
      setPendingSelectionId(clickedId);
      confirmationModal.open();
      return;
    }

    setSelectCostCenter(cc);
    setConfirmedSelection(clickedId);
    setPendingSelectionId(null);
  };

  const handleNodeToggle = (_, nodeIds) => {
    setExpandedNodes(nodeIds);
  };

  const ItemTree = (cc) => {
    const nextLevelKey = Object.keys(cc).find(
      key => key.startsWith('costCentersLevel') && Array.isArray(cc[key])
    );

    const children = nextLevelKey
      ? cc[nextLevelKey].map(child => ItemTree(child))
      : null;

    const hasChildren = !!(children && children.length > 0);
    const idStr = String(cc.id);
    const isExpanded = expandedNodes.includes(idStr);
    const isSelected = confirmedSelection === idStr;
    const isPending = pendingSelectionId === idStr;

    const icon = !hasChildren
      ? <FaFileAlt color="#ffc107" />
      : (isExpanded ? <FaRegFolderOpen color="#ffc107" /> : <FaRegFolder color="#ffc107" />);

    const labelStyle = {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      cursor: 'pointer',
      backgroundColor: isSelected ? '#e8f0fe' : (isPending ? '#fff7e6' : 'transparent'),
      border: isSelected ? '1px solid rgba(33, 150, 243, 0.2)' : '1px solid transparent',
      borderRadius: 6,
      padding: '6px 8px'
    };

    return (
      <TreeItem
        key={idStr}
        itemId={idStr}
        label={
          <div
            style={labelStyle}
            onClick={(e) => {
              e.stopPropagation();

              if (editMode) {
                handleTreeItemClick(cc);
                return;
              }

              if (hasChildren) {
                setExpandedNodes(prev => {
                  if (prev.includes(idStr)) {
                    return prev.filter(i => i !== idStr);
                  }
                  return [...prev, idStr];
                });
              }

              handleTreeItemClick(cc);
            }}
          >
            {icon}
            <Text>{` ${cc.structure} - ${cc.name.replace(/^\d+\s*-\s*/, '')}`}</Text>
          </div>
        }
      >
        {children}
      </TreeItem>
    );
  };

  return (
    <ApplicationLayout
      title="Centros de custo"
      isOld={true}
      contentFluid={true}
      RightComponent={
        <Button
          loading={loadingCostCenters}
          variant="secondary"
          className="py-2"
          onClick={getAndUpdateCostCentersBenner}
        >
          <Text weight={500} color="gray" type="regular">
            Importar do Benner
          </Text>
        </Button>
      }
    >
      <Row style={{ display: 'flex', alignItems: 'stretch', height: '100%', overflow: 'hidden' }}>
        <Col md={4} xl={4}>
          <TreeContainer>
            <Col md={6} xl={6} className='mb-4'>
              <Text size={18} weight={600} className='mb-3'>Centros de Custo</Text>
            </Col>
            <Col className='mb-4' md={11} xl={11}>
              <Filters
                setFilters={setFilters}
                filters={filters}
                setSelectCostCenter={setSelectCostCenter}
              />
            </Col>
            <div className="tree-scroll">
              {
                loading || loadingInitial ? (
                  <Col style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <Skeleton variant="rect" width="100%" style={{ borderRadius: 10 }} />
                    <Skeleton variant="rect" width="80%" style={{ borderRadius: 10 }} />
                    <Skeleton variant="rect" width="60%" style={{ borderRadius: 10 }} />
                    <Skeleton variant="rect" width="40%" style={{ borderRadius: 10 }} />
                    <Skeleton variant="rect" width="20%" style={{ borderRadius: 10 }} />
                  </Col>
                ) : (
                  costCenters.length === 0 ? (
                    <Text color="#8a8f98">Nenhum dado para exibir</Text>
                  ) : (
                    <SimpleTreeView
                      expandedItems={expandedNodes}
                      onExpandedItemsChange={handleNodeToggle}
                      slots={{
                        collapseIcon: ExpandMoreOutlined,
                        expandIcon: ChevronRight,
                      }}
                    >
                      {costCenters.map(cc => ItemTree(cc))}
                    </SimpleTreeView>
                  )
                )
              }
            </div>
          </TreeContainer>
        </Col>

        <Divider
          orientation="vertical"
          flexItem
          sx={{
            alignSelf: 'stretch',
            width: 0,
            borderRightWidth: '1px',
            borderColor: '#e0e0e0',
          }}
        />

        {
          loading ? (
            <Col style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
              <Loading size={40} />
            </Col>
          ) : (
            Object?.keys(selectCostCenter)?.length > 0 ?
              <CardCostCenter
                costCenter={selectCostCenter}
                fetchCostCenters={fetchCostCenters}
                editMode={editMode}
                setEditMode={setEditMode}
                setSelectCostCenter={setSelectCostCenter}
              />
              : null
          )
        }
      </Row>
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => {
          confirmationModal.close();
          setPendingCostCenter(null);
          setPendingSelectionId(null);
        }}
        onConfirm={() => {
          confirmationModal.close();
          setEditMode(false);

          if (pendingCostCenter) {
            setSelectCostCenter(pendingCostCenter);
            setConfirmedSelection(String(pendingCostCenter.id));
            setPendingCostCenter(null);
            setPendingSelectionId(null);
          }
        }}
      />
    </ApplicationLayout>
  );
}

export default CostCenter;
